import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import { Check } from "lucide-react";
import { useCallback } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "./icon";

/**
 * A vertical list of mutually exclusive answers, each a card with an optional
 * description line. A2's `ChoiceList`, on Base UI's RadioGroup, so the list
 * is one tab stop and ↑/↓ move the selection.
 *
 * `value` may be `null`: an onboarding question starts unanswered, and no
 * card is selected until the person picks one.
 */

export interface ChoiceOption<T extends string> {
  /** The second, quieter line. */
  description?: string;
  label: string;
  value: T;
}

export interface ChoiceListProps<T extends string> {
  className?: string;
  disabled?: boolean;
  /** The group's accessible name. */
  label: string;
  onValueChange: (value: T) => void;
  options: readonly ChoiceOption<T>[];
  value: T | null;
}

export function ChoiceList<T extends string>({
  value,
  onValueChange,
  options,
  label,
  disabled,
  className,
}: ChoiceListProps<T>) {
  // The group only ever reports one of `options`; `null` is the unanswered
  // start, never a change.
  const select = useCallback(
    (next: T | null) => {
      if (next !== null) {
        onValueChange(next);
      }
    },
    [onValueChange]
  );
  return (
    <RadioGroup<T | null>
      aria-label={label}
      className={cx("flex flex-col gap-2", className)}
      disabled={disabled}
      onValueChange={select}
      value={value}
    >
      {options.map((option) => (
        <Radio.Root
          className="group a-press flex min-h-14 cursor-pointer select-none items-center gap-3 rounded-lg bg-surface px-4 py-3 text-left ring-1 ring-line transition-shadow hover:ring-line-strong data-disabled:cursor-not-allowed data-disabled:opacity-40 data-checked:ring-2 data-checked:ring-accent"
          key={option.value}
          value={option.value}
        >
          <span className="min-w-0 flex-1">
            <span className="block font-semibold text-base text-ink leading-snug">
              {option.label}
            </span>
            {option.description ? (
              <span className="mt-0.5 block text-ink-3 text-sm leading-snug">
                {option.description}
              </span>
            ) : null}
          </span>
          <span className="grid size-6 shrink-0 place-items-center rounded-full border-2 border-line-strong text-on-accent group-data-checked:border-accent group-data-checked:bg-accent">
            <Radio.Indicator>
              <Icon bold icon={Check} size={16} />
            </Radio.Indicator>
          </span>
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}
